/**
 * useWebAuthn — biometric authentication lifecycle.
 *
 *   isSupported       → whether the device has a platform authenticator
 *   credentials       → list of enrolled WebAuthn credentials for the user
 *   enrollBiometric() → register a new biometric credential
 *   authenticate()    → authenticate using an existing credential
 *   revoke()          → soft-delete a credential (sets revoked_at)
 *
 * Backend registration goes through the webauthn-enroll Edge Function.
 * Backend verification goes through the webauthn-verify Edge Function.
 * Credential listing reads directly from Supabase (RLS-protected).
 *
 * Pattern follows usePushSubscription: state + async actions + error handling
 * via AppError.
 */

import { useCallback, useEffect, useState }    from 'react'
import { useSupabase }                          from './useSupabase'
import { isPlatformAuthenticatorAvailable }     from '@/lib/webauthn/support'
import { createRegistration, getAssertion }     from '@/lib/webauthn/ceremony'
import { AppError }                             from '@/lib/errors/AppError'
import type { WebAuthnCredential }              from '@/lib/webauthn/types'

export interface WebAuthnState {
  supported:   boolean | null
  credentials: WebAuthnCredential[]
  loading:     boolean
  error:       AppError | null
}

export interface UseWebAuthnResult extends WebAuthnState {
  enrollBiometric(deviceName: string): Promise<void>
  authenticate(): Promise<void>
  revoke(credentialDbId: string): Promise<void>
  refresh(): Promise<void>
}

export function useWebAuthn(
  userId:   string | null,
  userName: string | null,
): UseWebAuthnResult {
  const supabase = useSupabase()
  const [state, setState] = useState<WebAuthnState>({
    supported:   null,
    credentials: [],
    loading:     false,
    error:       null,
  })

  const refresh = useCallback(async () => {
    const supported = await isPlatformAuthenticatorAvailable()
    setState((s) => ({ ...s, supported }))

    if (!userId) {
      setState((s) => ({ ...s, credentials: [] }))
      return
    }

    const { data, error } = await supabase
      .from('webauthn_credentials')
      .select('id, credential_id, device_name, transports, created_at, last_used_at')
      .eq('user_id', userId)
      .is('revoked_at', null)
      .order('created_at', { ascending: false })

    if (error) {
      setState((s) => ({
        ...s,
        error: new AppError({
          code:        'CREDENTIAL_FETCH_FAILED',
          message:     error.message,
          userMessage: 'Could not load biometric credentials.',
          status:      500,
          cause:       error,
        }),
      }))
      return
    }

    setState((s) => ({ ...s, credentials: data ?? [] }))
  }, [supabase, userId])

  // Every setState inside `refresh` follows an await, so the mount probe is
  // an async continuation rather than a synchronous effect write. The IIFE
  // states that explicitly instead of leaving it implicit in `void refresh()`.
  //
  // This hook still owns its own fetch state by hand, which CLAUDE.md §3 says
  // should be React Query. Converting it (and usePushSubscription) is real
  // work that changes refetch timing on the biometric sign-in path, so it is
  // filed as a follow-up rather than smuggled into a lint upgrade.
  useEffect(() => {
    void (async () => { await refresh() })()
  }, [refresh])

  const enrollBiometric = useCallback(async (deviceName: string) => {
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      if (!userId || !userName) {
        throw new AppError({
          code:        'NOT_AUTHENTICATED',
          message:     'User must be authenticated to enroll biometric',
          userMessage: 'Please sign in to set up biometric authentication.',
          status:      401,
        })
      }

      const supported = await isPlatformAuthenticatorAvailable()
      if (!supported) {
        throw new AppError({
          code:        'UNSUPPORTED',
          message:     'Platform authenticator not available',
          userMessage: 'Biometric authentication is not supported on this device.',
          status:      415,
        })
      }

      const payload = await createRegistration({
        supabase,
        userId,
        userName,
        deviceName,
      })

      const { error } = await supabase.functions.invoke('webauthn-enroll', {
        body: payload,
      })
      if (error) {
        throw new AppError({
          code:        'ENROLL_FAILED',
          message:     error.message,
          userMessage: 'Could not register your biometric credential. Please try again.',
          status:      500,
        })
      }

      await refresh()
      setState((s) => ({ ...s, loading: false, error: null }))
    } catch (e: unknown) {
      const err = e instanceof AppError
        ? e
        : new AppError({
            code:        'ENROLL_FAILED',
            message:     e instanceof Error ? e.message : 'Unknown error during enrollment',
            userMessage: 'Biometric enrollment failed. Please try again.',
            status:      500,
            cause:       e,
          })
      setState((s) => ({ ...s, loading: false, error: err }))
      throw err
    }
  }, [supabase, userId, userName, refresh])

  const authenticate = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      if (state.credentials.length === 0) {
        throw new AppError({
          code:        'NO_CREDENTIALS',
          message:     'No WebAuthn credentials enrolled',
          userMessage: 'No biometric credentials are registered. Please enroll first.',
          status:      404,
        })
      }

      const payload = await getAssertion({
        supabase,
        allowCredentials: state.credentials.map((c) => ({
          id:         c.credential_id,
          transports: c.transports,
        })),
      })

      const { error } = await supabase.functions.invoke('webauthn-verify', {
        body: payload,
      })
      if (error) {
        throw new AppError({
          code:        'VERIFY_FAILED',
          message:     error.message,
          userMessage: 'Biometric verification failed. Please try again.',
          status:      401,
        })
      }

      await refresh()
      setState((s) => ({ ...s, loading: false, error: null }))
    } catch (e: unknown) {
      const err = e instanceof AppError
        ? e
        : new AppError({
            code:        'VERIFY_FAILED',
            message:     e instanceof Error ? e.message : 'Unknown error during verification',
            userMessage: 'Biometric verification failed. Please try again.',
            status:      500,
            cause:       e,
          })
      setState((s) => ({ ...s, loading: false, error: err }))
      throw err
    }
  }, [supabase, state.credentials, refresh])

  const revoke = useCallback(async (credentialDbId: string) => {
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const { error } = await supabase.functions.invoke('webauthn-enroll', {
        body: { action: 'revoke', credential_db_id: credentialDbId },
      })
      if (error) {
        throw new AppError({
          code:        'REVOKE_FAILED',
          message:     error.message,
          userMessage: 'Could not remove this credential. Please try again.',
          status:      500,
        })
      }

      await refresh()
      setState((s) => ({ ...s, loading: false, error: null }))
    } catch (e: unknown) {
      const err = e instanceof AppError
        ? e
        : new AppError({
            code:        'REVOKE_FAILED',
            message:     e instanceof Error ? e.message : 'Unknown error during revocation',
            userMessage: 'Could not remove this credential. Please try again.',
            status:      500,
            cause:       e,
          })
      setState((s) => ({ ...s, loading: false, error: err }))
      throw err
    }
  }, [supabase, refresh])

  return { ...state, enrollBiometric, authenticate, revoke, refresh }
}
